import { Component } from '@theme/component';

/**
 * Mobile-only carousel navigation for custom sections.
 *
 * @typedef {object} MobileCarouselRefs
 * @property {HTMLElement} scroller
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 * @property {HTMLElement} [current]
 * @property {HTMLElement} [total]
 *
 * @extends {Component<MobileCarouselRefs>}
 */
class MobileCarouselComponent extends Component {
  requiredRefs = ['scroller'];

  /** @type {number} */
  #index = 0;

  /** @type {number} */
  #raf = 0;

  /** @type {MutationObserver | null} */
  #childObserver = null;

  connectedCallback() {
    super.connectedCallback();

    const { scroller } = this.refs;
    if (!(scroller instanceof HTMLElement)) return;

    scroller.addEventListener('scroll', this.#onScroll, { passive: true });
    this.#childObserver = new MutationObserver(() => this.#syncFromScroll());
    this.#childObserver.observe(scroller, { childList: true });
    this.#syncFromScroll();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    const { scroller } = this.refs;
    if (scroller instanceof HTMLElement) {
      scroller.removeEventListener('scroll', this.#onScroll);
    }

    this.#childObserver?.disconnect();
    this.#childObserver = null;

    if (this.#raf) cancelAnimationFrame(this.#raf);
  }

  /**
   * @param {Event} [_event]
   */
  previous(_event) {
    this.#go(this.#index - 1);
  }

  /**
   * @param {Event} [_event]
   */
  next(_event) {
    this.#go(this.#index + 1);
  }

  /**
   * @returns {HTMLElement[]}
   */
  #getSlides() {
    const { scroller } = this.refs;
    if (!(scroller instanceof HTMLElement)) return [];

    return [...scroller.children].filter((child) => {
      if (!(child instanceof HTMLElement)) return false;
      if (child.matches('style, script, template, noscript')) return false;
      if (child.hidden || child.hasAttribute('shopify-block-empty')) return false;
      if (child.querySelector(':scope > [shopify-block-empty]')) return false;
      return getComputedStyle(child).display !== 'none';
    });
  }

  /**
   * @param {number} index
   */
  #go(index) {
    const { scroller } = this.refs;
    const slides = this.#getSlides();
    if (!(scroller instanceof HTMLElement) || slides.length === 0) return;

    const next = Math.max(0, Math.min(index, slides.length - 1));
    const slide = slides[next];
    if (!(slide instanceof HTMLElement)) return;

    const left =
      slide.getBoundingClientRect().left - scroller.getBoundingClientRect().left + scroller.scrollLeft;

    scroller.scrollTo({ left, behavior: 'smooth' });
    this.#setIndex(next, slides.length);
  }

  #onScroll = () => {
    if (this.#raf) return;

    this.#raf = requestAnimationFrame(() => {
      this.#raf = 0;
      this.#syncFromScroll();
    });
  };

  #syncFromScroll() {
    const { scroller } = this.refs;
    const slides = this.#getSlides();
    if (!(scroller instanceof HTMLElement) || slides.length === 0) return;

    const scrollerRect = scroller.getBoundingClientRect();
    const viewportStart = scrollerRect.left;
    const viewportEnd = scrollerRect.right;

    let bestIndex = 0;
    let bestOverlap = -1;

    slides.forEach((slide, index) => {
      const rect = slide.getBoundingClientRect();
      const overlap = Math.min(rect.right, viewportEnd) - Math.max(rect.left, viewportStart);
      if (overlap > bestOverlap) {
        bestOverlap = overlap;
        bestIndex = index;
      }
    });

    this.#setIndex(bestIndex, slides.length);
  }

  /**
   * @param {number} index
   * @param {number} total
   */
  #setIndex(index, total) {
    this.#index = index;

    const { current, total: totalEl, previous, next } = this.refs;

    if (current) current.textContent = String(index + 1);
    if (totalEl) totalEl.textContent = String(total);

    if (previous) previous.disabled = index <= 0;
    if (next) next.disabled = index >= total - 1;
  }
}

if (!customElements.get('mobile-carousel-component')) {
  customElements.define('mobile-carousel-component', MobileCarouselComponent);
}
